import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Package,
  ShoppingCart,
  Users,
  Truck,
  RefreshCw,
  Calendar,
  AlertTriangle,
  Bell,
  Plus,
  Trash2,
  Settings,
  X,
  Loader2,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  PieChart as PieChartIcon
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = {
  primary: ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'],
  success: ['#22c55e', '#4ade80', '#86efac', '#bbf7d0', '#dcfce7'],
  warning: ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#fef3c7'],
  purple: ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'],
  pink: ['#ec4899', '#f472b6', '#f9a8d4', '#fbcfe8', '#fce7f3']
};

const PIE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }}></span>
            {entry.name}: {formatter ? formatter(entry.value) : entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Empty state component
const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-8 h-8 text-slate-400" />
    </div>
    <h4 className="text-slate-700 font-medium mb-1">{title}</h4>
    <p className="text-slate-500 text-sm max-w-xs">{description}</p>
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export default function Reports() {
  const { user, loading: authLoading } = useAuth();
  const { companyId, selectedCompany } = useCompany();
  
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [trendView, setTrendView] = useState('weekly');
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [topSales, setTopSales] = useState([]);
  const [topPurchases, setTopPurchases] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [purchaseTrend, setPurchaseTrend] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reorderFrequency, setReorderFrequency] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [reorderSettings, setReorderSettings] = useState([]);
  
  // Modal states
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [newSetting, setNewSetting] = useState({ material_name: '', reminder_days: 30 });
  const [submitting, setSubmitting] = useState(false);

  const getDateRange = useCallback(() => {
    const today = new Date();
    let start = null;
    let end = today.toISOString().split('T')[0];
    
    if (dateRange === 'custom') {
      return { start: customStart || null, end: customEnd || null };
    }
    
    if (dateRange === 'all') {
      return { start: null, end: null };
    }
    
    const days = parseInt(dateRange);
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - days);
    start = startDate.toISOString().split('T')[0];
    
    return { start, end };
  }, [dateRange, customStart, customEnd]);

  const fetchAllData = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    const { start, end } = getDateRange();
    
    const params = new URLSearchParams({ company_id: companyId });
    if (start) params.append('start_date', start);
    if (end) params.append('end_date', end);
    
    try {
      const [
        summaryRes,
        topSalesRes,
        topPurchasesRes,
        salesTrendRes,
        purchaseTrendRes,
        suppliersRes,
        customersRes,
        reorderRes,
        remindersRes,
        settingsRes
      ] = await Promise.all([
        fetch(`${API_URL}/api/reports/summary?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/reports/top-sales?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/reports/top-purchases?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/reports/sales-trend?${params}&group_by=${trendView}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/reports/purchase-trend?${params}&group_by=${trendView}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/reports/supplier-breakdown?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/reports/customer-breakdown?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/reports/reorder-frequency?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/reports/reorder-reminders?company_id=${companyId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/reports/reorder-settings?company_id=${companyId}`, { credentials: 'include' })
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (topSalesRes.ok) setTopSales((await topSalesRes.json()).top_sales || []);
      if (topPurchasesRes.ok) setTopPurchases((await topPurchasesRes.json()).top_purchases || []);
      if (salesTrendRes.ok) setSalesTrend((await salesTrendRes.json()).trend || []);
      if (purchaseTrendRes.ok) setPurchaseTrend((await purchaseTrendRes.json()).trend || []);
      if (suppliersRes.ok) setSuppliers((await suppliersRes.json()).suppliers || []);
      if (customersRes.ok) setCustomers((await customersRes.json()).customers || []);
      if (reorderRes.ok) setReorderFrequency((await reorderRes.json()).materials || []);
      if (remindersRes.ok) setReminders((await remindersRes.json()).reminders || []);
      if (settingsRes.ok) setReorderSettings((await settingsRes.json()).settings || []);

    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [companyId, getDateRange, trendView]);

  useEffect(() => {
    if (!authLoading && user && companyId) {
      fetchAllData();
    }
  }, [authLoading, user, companyId, fetchAllData]);

  const handleAddSetting = async () => {
    if (!newSetting.material_name.trim()) {
      toast.error('Please enter a material name');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/reorder-settings?company_id=${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newSetting)
      });
      
      if (response.ok) {
        toast.success('Reorder reminder added');
        setNewSetting({ material_name: '', reminder_days: 30 });
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add reminder');
      }
    } catch (error) {
      toast.error('Failed to add reminder');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSetting = async (settingId) => {
    try {
      const response = await fetch(`${API_URL}/api/reports/reorder-settings/${settingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Reminder deleted');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  // Check if there's any data at all
  const hasAnyData = (summary?.sales?.total_orders > 0) || (summary?.purchases?.total_orders > 0) || 
                     topSales.length > 0 || topPurchases.length > 0 || 
                     salesTrend.length > 0 || purchaseTrend.length > 0;

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Analytics & Reports</h1>
            <p className="text-slate-500 mt-1">
              {selectedCompany?.name} • Performance insights
            </p>
          </div>
          <Button onClick={fetchAllData} variant="outline" className="gap-2" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
            Refresh Data
          </Button>
        </div>

        {/* Date Range Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Period:</span>
            </div>
            <div className="flex gap-2">
              {[
                { value: '7', label: '7 Days' },
                { value: '30', label: '30 Days' },
                { value: '90', label: '90 Days' },
                { value: 'all', label: 'All Time' }
              ].map((option) => (
                <Button
                  key={option.value}
                  variant={dateRange === option.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setDateRange(option.value)}
                  className={dateRange === option.value ? '' : 'text-slate-600'}
                >
                  {option.label}
                </Button>
              ))}
              <Button
                variant={dateRange === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDateRange('custom')}
                className={dateRange === 'custom' ? '' : 'text-slate-600'}
              >
                Custom
              </Button>
            </div>
            
            {dateRange === 'custom' && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-36 h-9"
                />
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-36 h-9"
                />
                <Button size="sm" onClick={fetchAllData}>Apply</Button>
              </div>
            )}
            
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-slate-500">View:</span>
              <div className="flex bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setTrendView('daily')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    trendView === 'daily' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Daily
                </button>
                <button
                  onClick={() => setTrendView('weekly')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    trendView === 'weekly' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Weekly
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
              </div>
              {summary?.sales?.total_orders > 0 && (
                <span className="flex items-center text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  <ArrowUpRight className="w-3 h-3 mr-1" />
                  Active
                </span>
              )}
            </div>
            <div className="text-2xl font-bold text-slate-900">{formatNumber(summary?.sales?.total_orders || 0)}</div>
            <div className="text-sm text-slate-500 mt-1">Sales Orders</div>
            <div className="text-xs text-blue-600 mt-2 font-medium">
              {summary?.sales?.unique_customers || 0} unique customers
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900">{formatNumber(Math.round(summary?.sales?.total_quantity || 0))}</div>
            <div className="text-sm text-slate-500 mt-1">Items Sold</div>
            <div className="text-xs text-green-600 mt-2 font-medium">
              {summary?.sales?.unique_items || 0} unique products
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900">{formatNumber(summary?.purchases?.total_orders || 0)}</div>
            <div className="text-sm text-slate-500 mt-1">Purchase Orders</div>
            <div className="text-xs text-amber-600 mt-2 font-medium">
              {summary?.purchases?.unique_suppliers || 0} suppliers
            </div>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <div className="text-2xl font-bold text-slate-900">{formatNumber(Math.round(summary?.purchases?.total_quantity || 0))}</div>
            <div className="text-sm text-slate-500 mt-1">Materials Received</div>
            <div className="text-xs text-purple-600 mt-2 font-medium">
              {summary?.purchases?.unique_materials || 0} unique materials
            </div>
          </div>
        </div>

        {/* Reorder Reminders Alert */}
        {reminders.length > 0 && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-5 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-red-800">Reorder Alerts</h3>
                  <p className="text-sm text-red-600">{reminders.length} materials need attention</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowSettingsModal(true)} className="gap-1 border-red-200 text-red-700 hover:bg-red-100">
                <Settings className="w-4 h-4" />
                Manage
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {reminders.slice(0, 6).map((item, idx) => (
                <div key={idx} className="bg-white rounded-lg p-3 border border-red-100 shadow-sm">
                  <div className="font-medium text-slate-900 truncate">{item.material_name}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-500">Last: {item.last_order}</span>
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                      {item.days_since}d ago
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Charts Grid */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Top Sales Chart */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900">Top Selling Products</h3>
              </div>
              {topSales.length > 0 && (
                <span className="text-xs text-slate-500">{topSales.length} products</span>
              )}
            </div>
            {topSales.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={topSales.slice(0, 8)} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis 
                    dataKey="item_name" 
                    type="category" 
                    width={120} 
                    tick={{ fontSize: 11, fill: '#475569' }} 
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => value.length > 18 ? value.substring(0, 18) + '...' : value}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total_quantity" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Quantity" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState 
                icon={ShoppingCart}
                title="No sales data yet"
                description="Sales data will appear here once you start recording transactions"
              />
            )}
          </div>

          {/* Top Purchases Chart */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Package className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-slate-900">Top Purchased Materials</h3>
              </div>
              {topPurchases.length > 0 && (
                <span className="text-xs text-slate-500">{topPurchases.length} materials</span>
              )}
            </div>
            {topPurchases.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={topPurchases.slice(0, 8)} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis 
                    dataKey="material_name" 
                    type="category" 
                    width={120} 
                    tick={{ fontSize: 11, fill: '#475569' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => value.length > 18 ? value.substring(0, 18) + '...' : value}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total_quantity" fill="#22c55e" radius={[0, 4, 4, 0]} name="Quantity" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState 
                icon={Package}
                title="No purchase data yet"
                description="Purchase data will appear here once you start recording raw materials"
              />
            )}
          </div>
        </div>

        {/* Trend Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Sales Trend */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900">Sales Trend</h3>
              </div>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                {trendView === 'daily' ? 'Daily' : 'Weekly'}
              </span>
            </div>
            {salesTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={salesTrend} margin={{ left: 0, right: 10 }}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={formatDate} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="quantity" stroke="#3b82f6" strokeWidth={2} fill="url(#salesGradient)" name="Quantity" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState 
                icon={Activity}
                title="No trend data available"
                description="Trend analysis will show here as you record more sales over time"
              />
            )}
          </div>

          {/* Purchase Trend */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-slate-900">Purchase Trend</h3>
              </div>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                {trendView === 'daily' ? 'Daily' : 'Weekly'}
              </span>
            </div>
            {purchaseTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={purchaseTrend} margin={{ left: 0, right: 10 }}>
                  <defs>
                    <linearGradient id="purchaseGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={formatDate} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="quantity" stroke="#22c55e" strokeWidth={2} fill="url(#purchaseGradient)" name="Quantity" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState 
                icon={Activity}
                title="No trend data available"
                description="Trend analysis will show here as you record more purchases over time"
              />
            )}
          </div>
        </div>

        {/* Breakdown Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Supplier Breakdown */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <Truck className="w-4 h-4 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Supplier Distribution</h3>
            </div>
            {suppliers.length > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="45%" height={220}>
                  <PieChart>
                    <Pie
                      data={suppliers.slice(0, 6)}
                      dataKey="total_quantity"
                      nameKey="supplier"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={50}
                    >
                      {suppliers.slice(0, 6).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2 overflow-y-auto max-h-[220px] pl-4">
                  {suppliers.slice(0, 8).map((s, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }}></div>
                      <span className="text-sm text-slate-700 truncate flex-1">{s.supplier || 'Unknown'}</span>
                      <span className="text-sm font-medium text-slate-900">{formatNumber(Math.round(s.total_quantity))}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState 
                icon={Truck}
                title="No supplier data"
                description="Supplier breakdown will show here once you have purchase records"
              />
            )}
          </div>

          {/* Customer Breakdown */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Customer Distribution</h3>
            </div>
            {customers.length > 0 ? (
              <div className="flex items-center">
                <ResponsiveContainer width="45%" height={220}>
                  <PieChart>
                    <Pie
                      data={customers.slice(0, 6)}
                      dataKey="total_quantity"
                      nameKey="customer"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={50}
                    >
                      {customers.slice(0, 6).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2 overflow-y-auto max-h-[220px] pl-4">
                  {customers.slice(0, 8).map((c, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }}></div>
                      <span className="text-sm text-slate-700 truncate flex-1">{c.customer || 'Unknown'}</span>
                      <span className="text-sm font-medium text-slate-900">{formatNumber(Math.round(c.total_quantity))}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState 
                icon={Users}
                title="No customer data"
                description="Customer breakdown will show here once you have sales records"
              />
            )}
          </div>
        </div>

        {/* Reorder Frequency Table */}
        {reorderFrequency.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <RefreshCw className="w-4 h-4 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Reorder Frequency Analysis</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Material</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Orders</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Qty</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Suppliers</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Order</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg. Interval</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {reorderFrequency.slice(0, 10).map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-900">{item.material_name}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full">
                          {item.order_count}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-slate-600">{formatNumber(Math.round(item.total_quantity))}</td>
                      <td className="py-3 px-4 text-center text-slate-600">{item.supplier_count}</td>
                      <td className="py-3 px-4 text-center text-slate-600">{item.last_order || '-'}</td>
                      <td className="py-3 px-4 text-center">
                        {item.avg_days_between_orders ? (
                          <span className="text-slate-600">{item.avg_days_between_orders} days</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Data Overall Message */}
        {!hasAnyData && !loading && (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <BarChart3 className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Data Available</h3>
            <p className="text-slate-500 max-w-md mx-auto mb-6">
              Start recording sales and purchase data to see analytics and insights. 
              Upload invoices or create manual entries to populate this dashboard.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => window.location.href = '/invoices'}>
                <FileText className="w-4 h-4 mr-2" />
                Upload Invoice
              </Button>
              <Button onClick={() => window.location.href = '/tickets/new'}>
                <Plus className="w-4 h-4 mr-2" />
                New Entry
              </Button>
            </div>
          </div>
        )}

        {/* Reorder Settings Modal */}
        {showSettingsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl" data-testid="reorder-settings-modal">
              <div className="flex items-center justify-between p-5 border-b">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Bell className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Reorder Settings</h2>
                    <p className="text-sm text-slate-500">Manage reminder thresholds</p>
                  </div>
                </div>
                <button onClick={() => setShowSettingsModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="p-5">
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-slate-600">
                    Set custom reminder periods for materials. Get notified if a material hasn't been purchased within the specified days.
                  </p>
                </div>
                
                <div className="flex gap-2 mb-5">
                  <Input
                    placeholder="Material name"
                    value={newSetting.material_name}
                    onChange={(e) => setNewSetting(prev => ({ ...prev, material_name: e.target.value }))}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Days"
                    value={newSetting.reminder_days}
                    onChange={(e) => setNewSetting(prev => ({ ...prev, reminder_days: parseInt(e.target.value) || 30 }))}
                    className="w-24"
                    min="1"
                  />
                  <Button onClick={handleAddSetting} disabled={submitting}>
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
                
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {reorderSettings.length > 0 ? (
                    reorderSettings.map((setting) => (
                      <div key={setting.setting_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <span className="font-medium text-slate-900">{setting.material_name}</span>
                          <span className="text-sm text-slate-500 ml-2">({setting.reminder_days} days)</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSetting(setting.setting_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No custom reminders</p>
                      <p className="text-xs mt-1">Default: 30 days for all materials</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-5 border-t bg-slate-50 rounded-b-xl">
                <Button onClick={() => setShowSettingsModal(false)} className="w-full">
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
